import { useMemo, useState } from 'react';
import { DOMAINS, DOMAIN_ORDER, NODE_DETAILS, type DomainId } from './archData';
import { getDomainComponentIds } from './layout';

interface SearchHit {
  nodeId: string;
  domainId: DomainId;
  label: string;
}

interface DomainSidebarProps {
  collapsedDomains: Set<DomainId>;
  onToggleDomain: (domainId: DomainId) => void;
  onSelectDomain: (domainId: DomainId) => void;
  onFocusNode: (nodeId: string, domainId: DomainId) => void;
}

export default function DomainSidebar({
  collapsedDomains,
  onToggleDomain,
  onSelectDomain,
  onFocusNode,
}: DomainSidebarProps) {
  const [query, setQuery] = useState('');

  const hits = useMemo<SearchHit[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return Object.values(NODE_DETAILS)
      .filter(d =>
        d.label.toLowerCase().includes(q) ||
        d.role.toLowerCase().includes(q) ||
        d.keyFiles.some(f => f.toLowerCase().includes(q))
      )
      .map(d => ({ nodeId: d.id, domainId: d.domain, label: d.label }));
  }, [query]);

  return (
    <div style={{
      width: 280,
      flexShrink: 0,
      background: '#1e293b',
      borderRight: '1px solid #334155',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Recherche */}
      <div style={{ padding: 14, borderBottom: '1px solid #334155' }}>
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Rechercher un composant..."
          style={{
            width: '100%',
            background: '#0f172a',
            border: '1px solid #334155',
            borderRadius: 8,
            color: '#f1f5f9',
            fontSize: 13,
            padding: '8px 10px',
            boxSizing: 'border-box',
            outline: 'none',
            fontFamily: 'system-ui, sans-serif',
          }}
        />
      </div>

      {/* Résultats de recherche */}
      {query.trim() && (
        <div style={{ padding: '8px 10px', overflowY: 'auto', borderBottom: '1px solid #334155', maxHeight: '40%' }}>
          {hits.length === 0 ? (
            <p style={{ fontSize: 12, color: '#64748b', padding: '4px 6px' }}>Aucun résultat</p>
          ) : (
            hits.map(hit => (
              <button
                key={hit.nodeId}
                onClick={() => onFocusNode(hit.nodeId, hit.domainId)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  color: '#e2e8f0',
                  fontSize: 12,
                  padding: '6px 8px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontFamily: 'system-ui, sans-serif',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = '#334155')}
                onMouseLeave={e => (e.currentTarget.style.background = 'none')}
              >
                <span style={{ color: '#64748b' }}>{DOMAINS[hit.domainId].label} → </span>
                <strong>{hit.label}</strong>
              </button>
            ))
          )}
        </div>
      )}

      {/* Liste des domaines */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
        {DOMAIN_ORDER.map(domainId => {
          const domain = DOMAINS[domainId];
          const count = getDomainComponentIds(domainId).length;
          const collapsed = collapsedDomains.has(domainId);
          return (
            <div
              key={domainId}
              title="Poser une question à Copilot sur ce domaine"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 8px',
                borderRadius: 8,
                cursor: 'pointer',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = '#273449')}
              onMouseLeave={e => (e.currentTarget.style.background = 'none')}
              onClick={() => onSelectDomain(domainId)}
            >
              <button
                onClick={e => { e.stopPropagation(); onToggleDomain(domainId); }}
                aria-label={collapsed ? 'Déplier' : 'Replier'}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#94a3b8',
                  fontSize: 12,
                  cursor: 'pointer',
                  width: 16,
                  flexShrink: 0,
                }}
              >
                {collapsed ? '▸' : '▾'}
              </button>
              <div style={{
                width: 10, height: 10, borderRadius: 3, flexShrink: 0,
                background: domain.bg, border: `1px solid ${domain.border}`,
              }} />
              <span style={{ fontSize: 12.5, color: '#e2e8f0', fontFamily: 'system-ui, sans-serif', flex: 1 }}>
                {domain.label}
              </span>
              <span style={{ fontSize: 11, color: '#64748b' }}>{count}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
